import { tr, LANGS } from "../../i18n";
import { useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { LayoutDashboard, FileCheck2, Award, PlusCircle, UserCircle2, HelpCircle, LogOut, Menu, X, Globe, Check } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";

const LANG_NAMES = { en: "English", nl: "Nederlands", fr: "Français", de: "Deutsch" };

function LangSwitch({ idPrefix = "portal-lang" }) {
  const { lang = "en" } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const switchTo = (l) => {
    const rest = location.pathname.replace(/^\/(en|nl|fr|de)/, "") || "/";
    navigate(`/${l}${rest}` + location.search);
    setOpen(false);
  };
  return (
    <div className="relative" data-testid={`${idPrefix}-switch`}>
      <button
        onClick={() => setOpen(!open)}
        data-testid={`${idPrefix}-toggle`}
        className="flex items-center gap-1.5 border border-esaia-line px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-esaia-muted transition-colors hover:border-esaia-blue hover:text-esaia-blue"
        aria-label={tr("Language")}
      >
        <Globe size={14} /> {lang}
      </button>
      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-40 border border-esaia-line bg-white py-1 shadow-lg">
          {LANGS.map((l) => (
            <button
              key={l}
              onClick={() => switchTo(l)}
              data-testid={`${idPrefix}-${l}`}
              className={`flex w-full items-center justify-between px-4 py-2 text-sm transition-colors hover:bg-esaia-canvas ${l === lang ? "font-semibold text-esaia-blue" : "text-esaia-ink"}`}
            >
              {LANG_NAMES[l]}
              {l === lang && <Check size={14} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PortalLayout() {
  const {
    lang = "en"
  } = useParams();
  const {
    user,
    logout
  } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const links = [{
    to: "",
    label: "Dashboard",
    icon: LayoutDashboard,
    end: true
  }, {
    to: "assessments",
    label: "Assessments",
    icon: FileCheck2
  }, {
    to: "certificates",
    label: "Certificates",
    icon: Award
  }, {
    to: "request-service",
    label: "Request a Service",
    icon: PlusCircle
  }, {
    to: "account",
    label: "Account",
    icon: UserCircle2
  }];
  const doLogout = async () => {
    await logout();
    navigate(`/${lang}/login`);
  };
  const nav = <nav className="flex flex-1 flex-col gap-1 px-4 py-6" aria-label={tr("Portal")}>
      {links.map(l => <NavLink key={l.to} to={`/${lang}/portal${l.to ? `/${l.to}` : ""}`} end={l.end} onClick={() => setOpen(false)} data-testid={`portal-nav-${l.to || "dashboard"}`} className={({
      isActive
    }) => `flex items-center gap-3 border-l-2 px-4 py-2.5 text-sm transition-colors ${isActive ? "border-esaia-blue bg-esaia-blue/5 font-semibold text-esaia-blue" : "border-transparent text-esaia-muted hover:bg-esaia-surface hover:text-esaia-ink"}`}>
          <l.icon size={17} strokeWidth={1.7} />
          {tr(l.label)}
        </NavLink>)}
      <a href="mailto:info@esaia.org?subject=ESAIA%20Client%20Portal%20Support" data-testid="portal-help-button" className="flex items-center gap-3 border-l-2 border-transparent px-4 py-2.5 text-sm text-esaia-muted transition-colors hover:bg-esaia-surface hover:text-esaia-ink">
        <HelpCircle size={17} strokeWidth={1.7} />{tr("Help")}{" "}</a>
    </nav>;
  return <div className="flex min-h-screen bg-esaia-canvas" data-testid="client-portal">
      {/* Desktop sidebar */}
      <aside className="hidden w-64 shrink-0 flex-col border-r border-esaia-line bg-white lg:flex">
        <div className="border-b border-esaia-line px-6 py-5">
          <Link to={`/${lang}`}>
            <img src="/logo.png" alt={tr("ESAIA")} className="h-9 w-auto" />
          </Link>
          <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-esaia-subtle">{tr("Client Portal")}</p>
        </div>
        {nav}
        <div className="border-t border-esaia-line p-4">
          <button onClick={doLogout} data-testid="portal-logout-button" className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-esaia-muted transition-colors hover:text-red-600">
            <LogOut size={17} strokeWidth={1.7} />{" "}{tr("Log Out")}{" "}</button>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="flex min-h-screen flex-1 flex-col">
        <div className="flex items-center justify-between border-b border-esaia-line bg-white px-4 py-3 lg:hidden">
          <img src="/logo.png" alt={tr("ESAIA")} className="h-8 w-auto" />
          <div className="flex items-center gap-3">
            <LangSwitch idPrefix="portal-lang-mobile" />
            <button onClick={() => setOpen(!open)} aria-label={tr("Menu")} data-testid="portal-mobile-menu-toggle" className="text-esaia-ink">
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
        {open && <div className="border-b border-esaia-line bg-white lg:hidden" data-testid="portal-mobile-menu">
            {nav}
            <div className="border-t border-esaia-line p-4">
              <button onClick={doLogout} className="flex items-center gap-3 px-4 py-2 text-sm text-esaia-muted">
                <LogOut size={17} />{" "}{tr("Log Out")}{" "}</button>
            </div>
          </div>}

        {/* Topbar desktop */}
        <div className="hidden items-center justify-end gap-4 border-b border-esaia-line bg-white px-8 py-3.5 lg:flex">
          <LangSwitch idPrefix="portal-lang-desktop" />
          <div className="text-right">
            <p className="text-sm font-semibold text-esaia-ink" data-testid="portal-user-name">
              {user?.first_name} {user?.last_name}
            </p>
            <p className="text-xs text-esaia-subtle">{user?.company || user?.email}</p>
          </div>
          <span className="flex h-9 w-9 items-center justify-center border border-esaia-blue/30 bg-esaia-blue/5 font-mono text-xs font-semibold text-esaia-blue">
            {(user?.first_name?.[0] || "").toUpperCase()}
            {(user?.last_name?.[0] || "").toUpperCase()}
          </span>
        </div>

        <main className="flex-1 px-4 py-8 sm:px-8 lg:px-10">
          <Outlet />
        </main>
      </div>
    </div>;
}
